import { DocumentList, Typography } from "@king-ict/eupisi-ui/components";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { getRequestDocument } from "../../../api/Request/RequestApi";
import { DocumentType } from "../../../models/Request/DocumentType";
import RequestTypeEnum from "../../../models/Request/RequestTypeEnum";
import { Attachment } from "../../../models/Shared/Attachment";
import { useLazyGetAdditionalDocumentationQuery } from "../../../queries/requestQuery";
import { RequestInfoPanel } from "../../Request/RequestComponents/RequestInfoPanel";

interface Props {
  requestType: RequestTypeEnum;
  requestId: number;
  showOtherParentDocuments: boolean;
  showEdit: boolean;
  moveToSpecificStep?: () => void;
}

export default function DocumentationDetails({
  requestType,
  requestId,
  showOtherParentDocuments,
  showEdit,
  moveToSpecificStep,
}: Props) {
  const { t } = useTranslation();
  const [
    triggerAdditionalDocumentation,
    { data: documentation, isLoading },
  ] = useLazyGetAdditionalDocumentationQuery();

  const handleExpandablePanelClick = () => {
    if (!documentation && !isLoading) {
      triggerAdditionalDocumentation(requestId);
    }
  };

  const handleDocumentDownload = async (
    doc: Attachment,
    type: DocumentType
  ) => {
    if (doc && doc.id) {
      await getRequestDocument(doc.id, requestId, type);
    }
  };

  const getDocumentPanel = (
    title: string,
    type: DocumentType,
    documents?: Attachment[]
  ) => {
    return (
      <>
        <Col md={4}>
          <Typography variant="smallHeading">{title}</Typography>
          {documents && documents.length ? (
            <>
              <DocumentList
                documents={documents}
                fullWidth
                removable={false}
                onDocumentClick={(doc) => handleDocumentDownload(doc, type)}
              />
            </>
          ) : (
            <Typography variant="small">
              {t("documentUpload.noUploadedDocuments")}
            </Typography>
          )}
        </Col>
      </>
    );
  };

  if (!documentation) {
    return (
      <RequestInfoPanel
        title={t("documentUpload.additionalDocumentation")}
        showEdit={showEdit}
        isLoading={isLoading}
        onEditClick={moveToSpecificStep}
        onExpandablePanelClick={handleExpandablePanelClick}
      />
    );
  }

  return (
    <RequestInfoPanel
      title={t("documentUpload.additionalDocumentation")}
      showEdit={showEdit}
      isLoading={isLoading}
      onEditClick={moveToSpecificStep}
      onExpandablePanelClick={handleExpandablePanelClick}
    >
      <Row>
        {/*UniqueBodyOfExpertise*/}
        {requestType === RequestTypeEnum.TemporaryExemption &&
          getDocumentPanel(
            t("documentUpload.documentReview.uniqueBodyOfExpertiseDocuments"),
            DocumentType.UniqueBodyOfExpertise,
            documentation.uniqueBodyOfExpertiseAttachments
          )}
        {(requestType ===
          RequestTypeEnum.RegularEnrollmentForChildrenWithLearningDifficulties ||
          requestType === RequestTypeEnum.TemporaryExemption) &&
          getDocumentPanel(
            t("documentUpload.documentReview.medicalDocuments"),
            DocumentType.Medical,
            documentation.medicalAttachments
          )}
        {(requestType ===
          RequestTypeEnum.RegularEnrollmentForChildrenWithLearningDifficulties ||
          requestType === RequestTypeEnum.TemporaryExemption) &&
          getDocumentPanel(
            t("documentUpload.documentReview.psychologyDocuments"),
            DocumentType.Psychology,
            documentation.psychologyAttachments
          )}
        {(requestType ===
          RequestTypeEnum.RegularEnrollmentForChildrenWithLearningDifficulties ||
          requestType === RequestTypeEnum.TemporaryExemption) &&
          getDocumentPanel(
            t(
              "documentUpload.documentReview.educationalRehabilitationDocuments"
            ),
            DocumentType.EducationalRehabilitation,
            documentation.educationalRehabilitationAttachments
          )}
        {/*Child*/}
        {(requestType ===
          RequestTypeEnum.RegularEnrollmentForChildrenWithLearningDifficulties ||
          requestType === RequestTypeEnum.TemporaryExemption ||
          requestType === RequestTypeEnum.EarlyEnrollment) &&
          getDocumentPanel(
            t("documentUpload.documentReview.childDocuments"),
            DocumentType.Child,
            documentation.childAttachments
          )}
        {requestType === RequestTypeEnum.RegularEnrollment &&
          getDocumentPanel(
            t("documentUpload.documentReview.childDocuments"),
            DocumentType.Child,
            documentation.childAttachments
          )}
        {/*PsychophysicalConditionDecision*/}
        {requestType === RequestTypeEnum.EarlyEnrollment &&
          getDocumentPanel(
            t(
              "documentUpload.documentReview.psychophysicalConditionDecisionDocuments"
            ),
            DocumentType.PsychophysicalConditionDecision,
            documentation.psychophysicalConditionDecisionAttachments
          )}
        {/* Other parent/ Roditelj 2 */}
        {showOtherParentDocuments &&
          getDocumentPanel(
            t("documentUpload.documentReview.otherDocuments"),
            DocumentType.OtherParent,
            documentation.otherAttachments
          )}
      </Row>
    </RequestInfoPanel>
  );
}
